import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Space,
  Table,
  Modal,
  Select,
  AutoComplete,
  Row,
  Col,
  message,
  Switch,
  InputNumber,
  Tag,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PushpinOutlined,
} from "@ant-design/icons";
import axios from "axios";
import type { ColumnsType } from "antd/es/table";
import { Country, State, City } from 'country-state-city';

interface EmployerDetails {
  _id: string;
  name: string;
  imageName: string;
  contactNumber: string;
  aboutCompany: string;
  constactPersonName: string;
  comapnyWebsite: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface Job {
  _id?: string;
  title: string;
  company: string;
  experience: { min: number; max: number };
  location: string;
  employer: string;
  salary: string;
  tags: string[];
  skills: string[];
  qualification: string[];
  companydesc: string;
  employername: string;
  imageName?: any;
  isPublished: boolean;
  priority: number;
  department: string;
  departmentDescription?: string;
  responsibilities?: any;
  employerDetails?: EmployerDetails;
  description?: {
    qualification: string[];
    responsibilities: string[];
  };
  userPostDataInfo?: Array<{
    name: string;
    role: string;
    emailId: string;
    createdAt: string;
    updatedAt: string;
  }>;
}

const { TextArea } = Input;
const { Option } = Select;

// 1. UPDATED Options with Descriptions
const departmentOptions = [
  { 
    label: "Sales & Business Development", 
    value: "Sales & Business Development",
    description: "Responsible for driving business growth, identifying new market opportunities, and managing client relationships to achieve sales targets."
  },
  { 
    label: "Customer Relationship Management", 
    value: "Customer Relationship Management",
    description: "Focuses on managing interactions with current and potential customers to improve relationships, customer retention, and sales growth."
  },
  { 
    label: "Finance & Accounts", 
    value: "Finance & Accounts",
    description: "Manages the company's financial planning, record-keeping, billing, reporting, and ensures compliance with financial regulations."
  },
  { 
    label: "Legal & Compliance", 
    value: "Legal & Compliance",
    description: "Ensures the company operates within the law, manages legal documents, and mitigates legal risks and compliance issues."
  },
  { 
    label: "Projects & Construction", 
    value: "Projects & Construction",
    description: "Oversees the planning, design, and construction of projects, ensuring they are completed on time, within budget, and to quality standards."
  },
  { 
    label: "IT/ ITES", 
    value: "IT/ ITES",
    description: "Handles technical infrastructure, software development, system maintenance, and IT support services."
  },
  { 
    label: "Human Resource", 
    value: "Human Resource",
    description: "Manages recruitment, employee relations, performance management, benefits, and training to support the organization's workforce."
  },
  { 
    label: "Research, Analysis & Strategy", 
    value: "Research, Analysis & Strategy",
    description: "Conducts market research and data analysis to formulate strategic plans that drive organizational growth and efficiency."
  },
  { 
    label: "Marketing & Communication", 
    value: "Marketing & Communication",
    description: "Responsible for brand promotion, public relations, content creation, and marketing campaigns to engage the target audience."
  },
  { 
    label: "Hospitality & Facility Management", 
    value: "Hospitality & Facility Management",
    description: "Ensures the maintenance of physical facilities and provides hospitality services to create a comfortable environment for employees and guests."
  },
  { 
    label: "Corporate Social Responsibility", 
    value: "Corporate Social Responsibility",
    description: "Develops and oversees initiatives that promote environmental sustainability and social well-being in line with company values."
  },
  { 
    label: "Learning & Development", 
    value: "Learning & Development",
    description: "Designs and implements training programs to enhance employee skills, productivity, and career growth."
  },
];

const JobComponent: React.FC = () => {
  const [form] = Form.useForm();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [editJobIndex, setEditJobIndex] = useState<string | null>(null);
  const [isEmployerModalVisible, setIsEmployerModalVisible] = useState<boolean>(false);
  const [employers, setEmployers] = useState<any[]>([]);
  const [comapny, setcompany] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [publishFilter, setPublishFilter] = useState<string>("all");
  const [sortOrder, setSortOrder] = useState<"A-Z" | "Z-A">("A-Z");

  // --- LOCATION STATES ---
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<{ name: string; [key: string]: any }[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");
  const [selectedStateCode, setSelectedStateCode] = useState<string>("");

  const getUserRole = (): string => {
    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    return userDetails?.role || '';
  };
  const userRole = getUserRole();
  const isAdmin = userRole === 'admin';

  const [textModalVisible, setTextModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalTitle, setModalTitle] = useState("");

  const [skills, setSkills] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState("");

  // 2. NEW FUNCTION: Auto-fill description when department changes
  const handleDepartmentChange = (value: string) => {
    const selectedDept = departmentOptions.find(opt => opt.value === value);
    if (selectedDept && selectedDept.description) {
        // We set the value to the default description
        form.setFieldsValue({
            departmentDescription: selectedDept.description
        });
    }
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !skills.includes(newSkill.trim())) {
      setSkills([...skills, newSkill.trim()]);
      setNewSkill("");
      form.setFieldsValue({ skills: [...skills, newSkill.trim()] });
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    const updatedSkills = skills.filter((skill) => skill !== skillToRemove);
    setSkills(updatedSkills);
    form.setFieldsValue({ skills: updatedSkills });
  };

  const [jobTitles, setJobTitles] = useState([
    { label: "Mern Stack", value: "Mern Stack" },
    { label: "Guest Relation Executive", value: "Guest Relation Executive" },
    { label: "Executive Asst. to Director", value: "Executive Asst. to Director" },
    { label: "Executive", value: "Executive" },
    { label: "Residential Leasing/Resale", value: "Residential Leasing/Resale" },
    { label: "Sales Coordinator", value: "Sales Coordinator" },
    { label: "GM sales", value: "GM sales" },
    { label: "Front Office Executive", value: "Front Office Executive" },
    { label: "Project Manager", value: "Project Manager" },
    { label: "Assistant Sales Manager", value: "Assistant Sales Manager" },
    { label: "Business Marketing & Strategy", value: "Business Marketing & Strategy" },
    { label: "Digital Marketing", value: "Digital Marketing" },
    { label: "Graphic Designer ", value: "Graphic Designer " },
    { label: "Tele Caller", value: "Tele Caller" },
    { label: "Sales Manager", value: "Sales Manager" },
    { label: "Area Sales Manager", value: "Area Sales Manager" },
    { label: "Sales Executive", value: "Sales Executive" },
    { label: "Business Development Executive", value: "Business Development Executive" },
    { label: "Counsellor", value: "Counsellor" },
    { label: "Education Counseling", value: "Education Counseling" },
    { label: "HR Executive", value: "HR Executive" },
    { label: "Sales Head", value: "Sales Head" },
  ]);

  let fileName = "";

  // 1. INITIALIZE COUNTRIES WITH CUSTOM SORTING & DUBAI
  useEffect(() => {
    const allCountries = Country.getAllCountries();

    // Custom Entry for Dubai
    const dubaiCustom = {
      name: "Dubai",
      isoCode: "DUBAI_CUSTOM", 
      flag: "🇦🇪",
      phonecode: "971",
      currency: "AED",
      latitude: "25.2048",
      longitude: "55.2708",
      timezones: []
    };

    // Priority Codes
    const priorityIsoCodes = ["IN", "AE"];
    
    // Sort logic: Dubai -> India -> UAE -> Rest
    const priorityCountries = allCountries.filter((c) => priorityIsoCodes.includes(c.isoCode));
    const otherCountries = allCountries.filter((c) => !priorityIsoCodes.includes(c.isoCode));

    setCountries([dubaiCustom, ...priorityCountries, ...otherCountries]);
  }, []);

  // 2. HANDLE COUNTRY CHANGE
  const handleCountryChange = (countryCode: string) => {
    setSelectedCountryCode(countryCode);
    
    // Special Logic for Dubai Custom Option
    if (countryCode === "DUBAI_CUSTOM") {
        const dubaiState = { name: "Dubai", isoCode: "DU", countryCode: "AE", latitude: "", longitude: "" };
        // @ts-ignore
        setStates([dubaiState]); 
        setCities([]); 
        form.setFieldsValue({ state: undefined, location: undefined });
    } else {
        const countryStates = State.getStatesOfCountry(countryCode);
        setStates(countryStates);
        setCities([]); 
        setSelectedStateCode(""); 
        form.setFieldsValue({ state: undefined, location: undefined }); 
    }
  };

  // 3. HANDLE STATE CHANGE
  const handleStateChange = (stateCode: string) => {
    setSelectedStateCode(stateCode);

    if (selectedCountryCode === "DUBAI_CUSTOM") {
        const dubaiCity = { name: "Dubai", countryCode: "AE", stateCode: "DU", latitude: "", longitude: "" };
        // @ts-ignore
        setCities([dubaiCity]);
        form.setFieldsValue({ location: undefined });
        return;
    }
    
    const selectedState = states.find(s => s.isoCode === stateCode);
    let stateCities = City.getCitiesOfState(selectedCountryCode, stateCode);

    if (stateCities.length === 0 && selectedState) {
       stateCities = [
           { 
             name: selectedState.name, 
             countryCode: selectedCountryCode, 
             stateCode: stateCode,
             latitude: "", 
             longitude: "" 
           }
       ];
    }

    // Apply the modification to city names
    setCities(modifyCityNames(stateCities));
    form.setFieldsValue({ location: undefined }); 
  };

  const modifyCityNames = (cities: { name: string; [key: string]: any }[]) => {
    return cities.map((city: { name: string; [key: string]: any }) => {
      if (city.name === "Gurgaon") {
        return { ...city, name: "Gurugram" };
      }
      return city;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `https://service.ireedindia.com/v1/get-employer?published=true&size=1000`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const responseData = await response.json();
        const employersArray = responseData.data || responseData;
        
        if (Array.isArray(employersArray)) {
          const formattedData = employersArray.map((item: any, index: number) => ({
            ...item,
            id: item.id || index,
            label: item.name,
            value: item.name,
          }));
          const emploer = employersArray.map((item: any, index: number) => ({
            ...item,
            id: item.id || index,
            label: item.name,
            value: item._id,
          }));
          setcompany(formattedData);
          setEmployers(emploer);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const truncateText = (text: string, wordLimit: number = 9) => {
    if (!text) return "N/A";
    const words = text.split(" ");
    if (words.length <= wordLimit) return text;
    const truncated = words.slice(0, wordLimit).join(" ");
    return `${truncated}...`;
  };

  const showFullText = (text: string, title: string) => {
    setModalContent(text);
    setModalTitle(title);
    setTextModalVisible(true);
  };

  const handleUpdate = async (jobId: string, formData: Job) => {
    try {
      const { employer, ...updateData } = formData;
      // Remove helper fields
      // @ts-ignore
      delete updateData.country; 
      // @ts-ignore
      delete updateData.state;

      const response = await axios.patch(
        `https://service.ireedindia.com/v1/edit-job/${jobId}`,
        updateData
      );
      const updatedJob = response.data;
      const updatedJobs = jobs.map((job) =>
        job._id === updatedJob._id ? updatedJob : job
      );
      setJobs(updatedJobs);
      message.success("Job updated successfully");
    } catch (error) {
      console.error("Error updating job:", error);
      message.error("Failed to update job");
    }
  };

  const onFinish = async (values: any) => {
    if (fileName) {
      values.imageName = fileName;
    }

    // Remove helper fields
    delete values.country;
    delete values.state;

    const responsibilities = values.responsibilities.split(".");
    const qualification = values.qualification.split(".");
    values.description = { qualification, responsibilities };

    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    const userId = userDetails?.userId;

    if (editJobIndex !== null) {
      try {
        await handleUpdate(editJobIndex, values);
        form.resetFields();
        setIsModalVisible(false);
        setEditJobIndex(null);
        // Reset location helpers
        setStates([]);
        setCities([]);
        setSelectedCountryCode("");
        setSelectedStateCode("");

        fetchDataFindJob();
      } catch (error) {
        console.error("Error updating job data:", error);
      }
    } else {
      const jobPayload = { ...values, userId: userId };
      
      axios
        .post(`https://service.ireedindia.com/v1/create-job`, jobPayload)
        .then((response) => {
          setJobs([...jobs, response.data]);
          form.resetFields();
          setIsModalVisible(false);
          setEditJobIndex(null);
          // Reset location helpers
          setStates([]);
          setCities([]);
          setSelectedCountryCode("");
          setSelectedStateCode("");

          setJobTitles((prevJobTitles) => [
            ...prevJobTitles,
            { label: values.title, value: values.title },
          ]);
          message.success("Job added successfully");
          fetchDataFindJob();
        })
        .catch((error) => {
          console.error("Error adding job:", error);
          message.error("Failed to add job");
        });
    }
  };

  const fetchDataFindJob = async () => {
    try {
      setLoading(true);

      let requestBody: any = {};

      if (searchQuery && searchQuery.trim()) {
        requestBody = {
          serachCompany: searchQuery.trim(),
          ascendingOrder: sortOrder,
        };

        if (isAdmin && publishFilter !== "all") {
          requestBody.isPublished = publishFilter === "published";
        }
      }
      else {
        requestBody = {
          pageNumber: pageNumber - 1,
          size: pageSize,
          sortKey: "company",
          sortDir: "ASEC",
          ascendingOrder: sortOrder,
        };

        if (isAdmin && publishFilter !== "all") {
          requestBody.isPublished = publishFilter === "published";
        }
      }

      const response = await axios.post(
        "https://service.ireedindia.com/v1/find-job",
        requestBody
      );

      setJobs(response.data.data || []);
      setTotal(response.data.totalItems || 0);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setJobs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployers = async () => {
    try {
      const response = await fetch(
        `https://service.ireedindia.com/v1/get-employer?published=true&size=1000`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch employer data");
      }
      const employerData = await response.json();
    } catch (error) {
      console.error("Error fetching employer data:", error);
    }
  };

  useEffect(() => {
    fetchEmployers();
    fetchDataFindJob();
  }, [pageNumber, pageSize, searchQuery, publishFilter,sortOrder]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPageNumber(1);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setPageNumber(1);
  };

  const handleTableChange = (pagination: any) => {
    setPageNumber(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleEdit = (record: Job) => {
    const qualificationText =
      record.description?.qualification?.join(". ") ||
      (record.qualification ? record.qualification.join(". ") : "");

    const responsibilitiesText =
      record.description?.responsibilities?.join(". ") ||
      (record.responsibilities ? record.responsibilities.join(". ") : "");

    form.setFieldsValue({
      title: record.title,
      experience: {
        min: record.experience?.min || 0,
        max: record.experience?.max || 3,
      },
      company: record.company,
      location: record.location, 
      salary: record.salary,
      isPublished: record.isPublished,
      priority: record.priority,
      tags: record.tags,
      skills: record.skills,
      qualification: qualificationText,
      companydesc: record.companydesc,
      responsibilities: responsibilitiesText,
      employername: record.employername,
      department: record.department,
      departmentDescription: record.departmentDescription,
      employer: record.employer,
    });

    setIsModalVisible(true);
    setEditJobIndex(record._id ?? null);
  };

  const handleDelete = async (jobId: string) => {
    try {
      await axios.delete(`https://service.ireedindia.com/v1/delete-job/${jobId}`);
      const updatedJobs = jobs.filter((job) => job._id !== jobId);
      setJobs(updatedJobs);
      message.success("Job deleted successfully");
      fetchDataFindJob();
    } catch (error) {
      console.error("Error deleting job:", error);
      message.error("Failed to delete job");
    }
  };

  const handleJobTags = (selectedTags: any) => {
    const tags = selectedTags.map((tag: any) => tag.label);
    form.setFieldsValue({ tags });
  };

  const handleDeleteClick = (id: any) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this job?");
    if (isConfirmed) {
      handleDelete(id);
    }
  };

  const columns: ColumnsType<Job> = [
    {
      title: "Company Name",
      dataIndex: "company",
      key: "company",
      render: (_: any, record: { company?: string; employerDetails?: { name?: string }; priority?: number; }) => {
        const companyName = record.company || record.employerDetails?.name || "N/A";
        const isPinned = record.priority && [1, 2, 3, 4, 5, 6 , 7, 8, 9, 10].includes(record.priority);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div>{companyName}</div>
            {isPinned && <PushpinOutlined style={{ color: "red" }} />}
          </div>
        );
      },
    },
    {
      title: "Image",
      dataIndex: "employerDetails",
      key: "image",
      render: (employerDetails: any, record: any) => {
        const imageName = employerDetails?.imageName || record.imageName || null;
        const imageUrl = imageName ? `https://service.ireedindia.com/image/${imageName}` : null;
        return imageUrl ? (
          <img
            src={imageUrl}
            alt="Employer"
            style={{ width: 80, height: 50, objectFit: "cover", borderRadius: 4, border: "1px solid #ccc" }}
            onError={(e) => { (e.target as HTMLImageElement).src = "/no-image.png"; }}
          />
        ) : ( "No Image" );
      },
    },
    {
      title: "Job Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      render: (experience) => {
        if (typeof experience === "object" && experience !== null) {
          return `${experience.min}-${experience.max} years`;
        }
        return experience || "N/A";
      },
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "Salary",
      dataIndex: "salary",
      key: "salary",
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      render: (department) => department || "N/A",
    },
    {
      title: "Department Description",
      dataIndex: "departmentDescription",
      key: "departmentDescription",
      render: (text: string) => {
        if (!text) return "N/A";
        const truncatedText = truncateText(text);
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>{truncatedText}</span>
            {text.length > 80 && (
              <Button type="text" icon={<EyeOutlined />} size="small"
                onClick={(e) => { e.stopPropagation(); showFullText(text, "Department Description"); }}
                style={{ marginLeft: 8 }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Pin Job",
      dataIndex: "priority",
      key: "priority",
      render: (priority) => priority ?? "N/A",
    },
    ...(isAdmin ? [{
      title: "Published",
      dataIndex: "isPublished",
      key: "isPublished",
      render: (isPublished: boolean) =>
        typeof isPublished === "boolean" ? (
          isPublished ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>
        ) : ( <Tag color="gray">N/A</Tag> ),
    }] : []),
    {
      title: "Description",
      dataIndex: "description",
      key: "responsibilities",
      render: (description, record) => {
        const fullText = description?.responsibilities?.filter(Boolean)?.join(", ") || "N/A";
        const truncatedText = truncateText(fullText);
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>{truncatedText}</span>
            {fullText !== "N/A" && fullText.length > 80 && (
              <Button type="text" icon={<EyeOutlined />} size="small"
                onClick={(e) => { e.stopPropagation(); showFullText(fullText, "Responsibilities"); }}
                style={{ marginLeft: 8 }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Qualification",
      dataIndex: "description",
      key: "qualification",
      render: (description, record) => {
        const fullText = description?.qualification?.filter(Boolean)?.join(", ") || "N/A";
        const truncatedText = truncateText(fullText);
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>{truncatedText}</span>
            {fullText !== "N/A" && fullText.length > 80 && (
              <Button type="text" icon={<EyeOutlined />} size="small"
                onClick={(e) => { e.stopPropagation(); showFullText(fullText, "Qualification"); }}
                style={{ marginLeft: 8 }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Skills",
      dataIndex: "skills",
      key: "skills",
      render: (skills: string[] | undefined) => {
        if (!skills || !skills.length) return "N/A";
        const fullText = skills.join(", ");
        const truncatedText = truncateText(fullText);
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>{truncatedText}</span>
            {fullText.length > 80 && (
              <Button type="text" icon={<EyeOutlined />} size="small"
                onClick={(e) => { e.stopPropagation(); showFullText(fullText, "Skills"); }}
                style={{ marginLeft: 8 }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (tags: string[] | undefined) => tags?.length ? tags.join(", ") : "N/A",
    },
    {
      title: "Employer ID",
      dataIndex: "employer",
      key: "employer",
    },
    ...(isAdmin ? [
      {
        title: "Created By",
        dataIndex: "userPostDataInfo",
        key: "createdBy",
        width: '15%',
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          return (
            <div style={{ fontSize: '12px' }}>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>{user.name}</div>
              <div style={{ color: '#6b7280' }}>{user.emailId}</div>
              <div style={{ color: '#1890ff', fontSize: '11px' }}>{user.role}</div>
            </div>
          );
        },
      },
      {
        title: "Created At",
        dataIndex: "userPostDataInfo",
        key: "createdAt",
        width: '12%',
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.createdAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
      {
        title: "Updated At",
        dataIndex: "userPostDataInfo",
        key: "updatedAt",
        width: '12%',
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.updatedAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
    ] : []),
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Job) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          {isAdmin && (
            <Button icon={<DeleteOutlined />} danger onClick={() => handleDeleteClick(record._id ?? "")} />
          )}
        </Space>
      ),
    },
  ];

  const CreateEmployerForm: React.FC<{ isVisible: boolean; onClose: () => void; }> = ({ isVisible, onClose }) => {
    const [employerForm] = Form.useForm();
    const onFinish = async (values: any) => {
      try {
        const response = await axios.post("https://service.ireedindia.com/v1/create-employer", values);
        message.success("Employer created successfully");
        fetchEmployers();
        fetchDataFindJob();
        onClose();
      } catch (error) {
        console.error("Error creating employer:", error);
      }
    };
    return (
      <Modal title="Add Employer" visible={isVisible} onCancel={() => { employerForm.resetFields(); onClose(); }} footer={null}>
        <Form form={employerForm} name="employer" onFinish={onFinish} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
          <Form.Item label="Employer Name" name="employername"><Input /></Form.Item>
          <Form.Item label="Company Name" name="company"><Input /></Form.Item>
          <Form.Item label="Description" name="companydesc"><TextArea /></Form.Item>
          <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
            <Button type="primary" htmlType="submit">Add</Button>
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4" style={{ gap: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Button type="primary" onClick={() => setIsModalVisible(true)}>Add Job</Button>
          {isAdmin && (
            <Select
              value={publishFilter}
              onChange={(value) => { setPublishFilter(value); setPageNumber(1); }}
              style={{ width: 180 }}
              placeholder="Filter by status"
            >
              <Option value="all">All Jobs</Option>
              <Option value="published">Published</Option>
              <Option value="unpublished">Unpublished</Option>
            </Select>
          )}
        </div>
        <div style={{ minWidth: 320, width: "50%" }}>
         <Input
            placeholder="Search by company name (e.g., DLF, IREED)..."
            allowClear
            value={searchInput}
            onChange={(e) => {
                const value = e.target.value;
                setSearchInput(value);
                setSearchQuery(value); 
                setPageNumber(1);
            }}
            />

        </div>
      </div>

      {searchQuery && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f7ff', borderRadius: 4 }}>
          <span>🔍 Searching for: <strong>"{searchQuery}"</strong></span>
          <span style={{ marginLeft: 16, color: '#666' }}>Found {total} job{total !== 1 ? 's' : ''}</span>
          <Button type="link" size="small" onClick={handleClearSearch} style={{ marginLeft: 8 }}>Clear search</Button>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={jobs}
        scroll={{ x: "max-content" }}
        rowKey="_id"
        loading={loading}
        pagination={{
          current: pageNumber,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100", "150", "200", "500"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
      />

      <Modal
        title={modalTitle}
        visible={textModalVisible}
        onCancel={() => setTextModalVisible(false)}
        footer={[<Button key="close" onClick={() => setTextModalVisible(false)}>Close</Button>]}
      >
        <p style={{ whiteSpace: "pre-wrap" }}>{modalContent}</p>
      </Modal>

      <Modal
        title={<div style={{ textAlign: "center", fontWeight: "bold" }}>
          {editJobIndex !== null ? "Edit Job" : "Add Job"}
        </div>}
        visible={isModalVisible}
        onCancel={() => { 
            form.resetFields(); 
            setIsModalVisible(false); 
            setEditJobIndex(null); 
            setStates([]);
            setCities([]);
            setSelectedCountryCode("");
            setSelectedStateCode("");
        }}
        footer={null}
        width={800}
      >
        <h1 className="text-center text-lg font-semibold p-4">All fields are compulsory to be filled</h1>
     <Form form={form} layout="vertical" onFinish={onFinish}>
  <Row gutter={16}>
    
    <Col span={12}>
      <Form.Item label="Employer" name="employer" hidden={editJobIndex !== null}
        rules={[{ required: true, message: "Please input the employer!" }]}>
        <Select optionFilterProp="children" showSearch
          filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          options={employers} placeholder="Select employer"
        />
      </Form.Item>
      {editJobIndex !== null && (
        <Form.Item label="Employer" name="employername"><Input disabled /></Form.Item>
      )}
    </Col>

    {/* --- MODIFIED LOCATION SECTION START --- */}
    <Col span={12}>
        <Row gutter={8}>
            <Col span={8}>
                <Form.Item label="Country" name="country">
                    <Select 
                        placeholder="Country" 
                        onChange={handleCountryChange}
                        showSearch
                        // FIX: Explicitly convert to string
                        filterOption={(input, option) => 
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {countries.map((country) => (
                            <Option 
                                key={country.isoCode} 
                                value={country.isoCode} 
                                label={country.name}
                            >
                                {country.flag} {country.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Col>
            <Col span={8}>
                <Form.Item label="State" name="state">
                    <Select 
                        placeholder="State" 
                        onChange={handleStateChange}
                        disabled={!selectedCountryCode}
                        showSearch
                        // FIX: Explicitly convert to string
                        filterOption={(input, option) => 
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {states.map((state) => (
                            <Option 
                                key={state.isoCode} 
                                value={state.isoCode}
                                label={state.name}
                            >
                                {state.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Col>
            <Col span={8}>
                <Form.Item label="City" name="location" rules={[{ required: true, message: "Select city!" }]}>
                    <Select 
                        placeholder="City"
                        showSearch
                        disabled={!selectedStateCode && !editJobIndex} 
                        // FIX: Explicitly convert to string
                        filterOption={(input, option) => 
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {cities.map((city) => (
                            <Option 
                                key={city.name} 
                                value={city.name}
                                label={city.name}
                            >
                                {city.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            </Col>
        </Row>
    </Col>
    {/* --- MODIFIED LOCATION SECTION END --- */}

    <Col span={12}>
      {/* 3. UPDATED Form Item with onChange handler */}
      <Form.Item label="Department" name="department" rules={[{ required: true, message: "Please input job department!" }]}>
        <Select placeholder="Select department" onChange={handleDepartmentChange}>
          {departmentOptions.map(option => (
            <Option key={option.value} value={option.value}>{option.label}</Option>
          ))}
        </Select>
      </Form.Item>
    </Col>

    <Col span={12}>
      <Form.Item label="Pin Job" name="priority" initialValue={0}
        rules={[{ required: false, message: "Please input priority number!" }, { type: "number", max: 10, message: "Priority can't be more than 10 digits!" }]}>
        <InputNumber min={0} max={10} precision={0} style={{ width: "100%" }} placeholder="Enter priority number" />
      </Form.Item>
    </Col>

    <Col span={24}>
      <Form.Item label="Job Title *" name="title" rules={[{ required: true, message: "Please input the job title!" }]}>
        <AutoComplete showSearch options={jobTitles} placeholder="Enter your job title"
          filterOption={(inputValue, option) => String(option?.value ?? '').toUpperCase().indexOf(inputValue.toUpperCase()) !== -1}
        />
      </Form.Item>
    </Col>

    <Col span={12}>
      <Form.Item label="Experience (Years)" required>
        <Input.Group compact>
          <Form.Item name={["experience", "min"]} noStyle
            rules={[{ required: true, message: "Min experience required" }]} initialValue={0}>
            <InputNumber min={0} max={30} placeholder="Min" style={{ width: "45%" }} addonAfter="Min" />
          </Form.Item>
          <Form.Item name={["experience", "max"]} noStyle
            rules={[{ required: true, message: "Max experience required" }]} initialValue={3}>
            <InputNumber min={0} max={30} placeholder="Max" style={{ width: "45%", marginLeft: "10%" }} addonAfter="Max" />
          </Form.Item>
        </Input.Group>
      </Form.Item>
    </Col>

    <Col span={12}>
      <Form.Item label="Salary (LPA) *" name="salary">
        <Select
          options={[
            { label: "Not Disclosed", value: "Not Disclosed" },
            { label: "0-3 LPA", value: "0-3 LPA" },
            { label: "3-6 LPA", value: "3-6 LPA" },
            { label: "6-10 LPA", value: "6-10 LPA" },
            { label: "10-15 LPA", value: "10-15 LPA" },
            { label: "15-20 LPA", value: "15-20 LPA" },
            { label: "20-30 LPA", value: "20-30 LPA" },
            { label: "30-50 LPA", value: "30-50 LPA" },
            { label: "50-75 LPA", value: "50-75 LPA" },
            { label: "75-100 LPA", value: "75-100 LPA" },
            { label: "1-5 Cr", value: "100-500 Cr" },
            { label: "5+ Cr", value: "500+ Cr" },
          ]}
          placeholder="Select salary"
        />
      </Form.Item>
    </Col>

    <Col span={24}>
      <Form.Item label="Job Description *" name="responsibilities"
        rules={[{ required: true, message: "Please input description!" }]}>
        <TextArea rows={4} placeholder="List job responsibilities" />
      </Form.Item>
    </Col>

    <Col span={24}>
      <Form.Item label="Skills" name="skills">
        <div>
          <Input value={newSkill} onChange={(e) => setNewSkill(e.target.value)} placeholder="Enter a skill"
            style={{ width: "70%", marginRight: "8px" }}
          />
          <Button type="primary" onClick={handleAddSkill}>Add</Button>
          <div style={{ marginTop: "8px" }}>
            {skills.map((skill) => (
              <Tag key={skill} closable onClose={() => handleRemoveSkill(skill)} style={{ marginBottom: "4px" }}>
                {skill}
              </Tag>
            ))}
          </div>
        </div>
      </Form.Item>
    </Col>

    <Col span={24}>
      <Form.Item label="Highest Qualification *" name="qualification"
        rules={[{ required: true, message: "Please input qualification!" }]}>
        <TextArea rows={3} placeholder="Minimum required qualification" />
      </Form.Item>
    </Col>

    <Col span={24}>
      <Form.Item label="Department Description" name="departmentDescription">
        <TextArea rows={3} placeholder="Add a description for the department" />
      </Form.Item>
    </Col>

  </Row>

  {isAdmin && (
    <Form.Item label="Published" name="isPublished" valuePropName="checked" initialValue={false}>
      <Switch />
    </Form.Item>
  )}

  <Form.Item>
    <Button type="primary" htmlType="submit">
      {editJobIndex !== null ? "Update Job" : "Add Job"}
    </Button>
  </Form.Item>
</Form>
      </Modal>

      <CreateEmployerForm isVisible={isEmployerModalVisible} onClose={() => setIsEmployerModalVisible(false)} />
    </div>
  );
};

export default JobComponent;
